/**
 * API client with Supabase session authentication
 *
 * Provides centralized API request handling with:
 * - Supabase session token in Authorization header
 * - Automatic token refresh on 401 responses via Supabase
 * - Request queuing during token refresh
 * - A RateLimitError with a "try again in N minutes" message on 429
 */

import { buildApiUrl } from './config';
import { supabase, getCachedSession } from '@/lib/supabase';
import { RateLimitError } from './rateLimit';

/**
 * A GET that came back with an error status: the server's message, and the
 * HTTP status so a caller can tell "not there" (403, 404) from a failure
 * worth retrying. Still an Error with the same message as before.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** The error is an API error with one of these statuses. */
export function hasStatus(error: unknown, ...statuses: number[]): boolean {
  return error instanceof ApiError && statuses.includes(error.status);
}

// Track token refresh state
let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

/**
 * Subscribe to token refresh completion
 * Used to queue requests during token refresh
 */
function subscribeTokenRefresh(callback: () => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all queued requests that token refresh is complete
 */
function onRefreshed(): void {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
}

/**
 * Notify all queued requests that token refresh failed
 * This prevents requests from hanging indefinitely
 */
function onRefreshFailed(): void {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
}

/**
 * Extended fetch options with retry flag
 */
interface FetchOptions extends RequestInit {
  _retry?: boolean;
}

/**
 * API client with Supabase authentication
 *
 * Automatically adds Authorization header with Supabase session token.
 * Handles 401 responses by refreshing the session via Supabase.
 * Throws RateLimitError on 429, so every caller shows the retry time.
 */
export async function apiClient(
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> {
  const response = await fetchWithAuth(endpoint, options);

  // Checked here rather than inside fetchWithAuth: an error thrown from the
  // retry after a token refresh would land in its catch, which redirects to login.
  if (response.status === 429) {
    throw new RateLimitError(response.headers.get('Retry-After'));
  }

  return response;
}

async function fetchWithAuth(
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> {
  // Build full URL - if endpoint is already a full URL, use it as-is
  const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
    ? endpoint
    : buildApiUrl(endpoint);

  // Get current session from cache (set by AuthContext)
  // Using cache avoids getSession() deadlock when called from onAuthStateChange
  const session = getCachedSession();

  // Set headers with Authorization if we have a session
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type for non-FormData bodies.
  // FormData needs the browser to auto-set multipart/form-data with boundary.
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  // Make request
  // cache: 'no-store' bypasses the browser HTTP cache so that React Query's
  // invalidateQueries() always fetches fresh data from the server.  React
  // Query's own staleTime already prevents unnecessary refetches, making the
  // browser cache redundant for API calls.
  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Keep for any cookie-based operations
    cache: 'no-store',
  });

  // Handle 401 (unauthorized) responses
  if (response.status === 401 && !options._retry) {
    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve) => {
        subscribeTokenRefresh(() => {
          // Retry original request after refresh
          resolve(
            fetchWithAuth(endpoint, {
              ...options,
              _retry: true, // prevent infinite loop
            })
          );
        });
      });
    }

    // Attempt token refresh via Supabase
    isRefreshing = true;

    try {
      const { error } = await supabase.auth.refreshSession();

      if (!error) {
        // Refresh successful - notify queued requests
        onRefreshed();
        isRefreshing = false;

        // Retry original request (which will get the new token)
        response = await fetchWithAuth(endpoint, {
          ...options,
          _retry: true, // Prevent infinite loop
        });
      } else {
        // Refresh failed - notify queued requests before redirect
        onRefreshFailed();
        isRefreshing = false;
        // Only redirect if not already on login page
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Session expired');
      }
    } catch (error) {
      // Notify queued requests on unexpected error
      onRefreshFailed();
      isRefreshing = false;
      // Only redirect if not already on login page (prevents infinite refresh loop)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  return response;
}

/**
 * Typed API client methods
 */
export const api = {
  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    const response = await apiClient(endpoint, {
      ...options,
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new ApiError(
        error.detail || `HTTP ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    return response.json();
  },

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const response = await apiClient(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(
        error.detail || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // 204 No Content - nothing to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  },

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const response = await apiClient(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(
        error.detail || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // 204 No Content - nothing to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  },

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const response = await apiClient(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(
        error.detail || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // 204 No Content - nothing to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  },

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    const response = await apiClient(endpoint, {
      ...options,
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(
        error.detail || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // 204 No Content - nothing to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  },
};
